import { useState } from "react";
import { SupportLayout } from "@/components/support-layout";
import {
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  Cpu,
  Radio,
  BarChart3,
} from "lucide-react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

const GOLD = "#C9A867";

const API_FEATURES = [
  {
    icon: Cpu,
    title: "Algorithmic Trading",
    desc: "Execute trades programmatically with our REST API.",
  },
  {
    icon: Radio,
    title: "Real-Time Data",
    desc: "Stream live prices, orderbooks, and fills via WebSocket.",
  },
  {
    icon: BarChart3,
    title: "Portfolio Management",
    desc: "Monitor positions, balances, and margin health.",
  },
];

export const ApiAccess = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [useCase, setUseCase] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit =
    name.trim() && email.trim() && useCase.trim() && status !== "sending";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "api-access",
          name: name.trim(),
          email: email.trim(),
          message: useCase.trim(),
        }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus("success");
        setName("");
        setEmail("");
        setUseCase("");
      } else {
        setStatus("error");
        setErrorMsg(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <SupportLayout>
      {/* ── Intro Section ── */}
      <section className="mb-10">
        <h1 className="text-3xl font-bold mb-3 text-gray-900 dark:text-gray-100">
          API Access
        </h1>
        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
          Request access to the Nordex REST and WebSocket APIs for programmatic
          trading, market data retrieval, and account management.
        </p>
      </section>

      {/* ── Request Form Card ── */}
      <section className="mb-10">
        <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 sm:px-8 py-5 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Request API access
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Tell us about your project and we'll review your request.
          </p>

          {status === "success" ? (
            <div className="py-8 text-center">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{ backgroundColor: `${GOLD}15` }}
              >
                <CheckCircle className="h-7 w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Request Submitted!
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                We'll review your API access request and get back to you
                shortly.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="text-sm font-medium hover:underline cursor-pointer"
                style={{ color: GOLD }}
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name + Email row */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="api-name"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Name / Organization{" "}
                    <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="api-name"
                    placeholder="Your name or organization"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="api-email"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="api-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>
              </div>

              {/* Use Case */}
              <div className="space-y-1.5">
                <label
                  htmlFor="api-usecase"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Use Case <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="api-usecase"
                  placeholder="Describe what you plan to build with the Nordex API..."
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all resize-none"
                />
              </div>

              {/* Error */}
              {status === "error" && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white h-11 px-8 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 cursor-pointer"
                style={{ background: GOLD }}
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Request API Access
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── What You Can Build ── */}
      <section className="mb-12">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          What You Can Build
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {API_FEATURES.map((item) => (
            <div
              key={item.title}
              className="p-5 bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm"
            >
              <div
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg mb-3"
                style={{ backgroundColor: `${GOLD}15` }}
              >
                <item.icon className="h-4 w-4" style={{ color: GOLD }} />
              </div>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-1">
                {item.title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
    </SupportLayout>
  );
};
